import { WritableAtom } from 'jotai';
import * as yup from 'yup';
import { KpiPeriod } from '../../data-models/company-financials.data-model';
import {
  createPanelPreferences,
  PanelPreferences,
  PreferenceKey,
  PreferenceScope,
  TearSheetGeneralPreferences,
  TearSheetPerformanceSummaryPreferences,
} from '../../data-models/preferences-v3.data-model';
import { preferenceAtom } from '../../util/preferences-service-v3';
import { ColumnMeta, createColumnMeta } from '../../types';
import {
  dateField,
  exitTypeField,
  multiplierField,
  percentField,
  usdShortField,
} from '../../schemas/common-schema-defs';
import { createFormField } from '../../view-models/form.view-model';
import { RendererType } from '../../data-models/field-definition';

export const AdditionalCompanySummaryColumns = new Map<string, ColumnMeta>(
  [
    createColumnMeta({
      path: 'company.primaryLocation',
      displayName: 'Primary Location',
      formatting: 'location',
    }),
    createColumnMeta({
      path: 'company.secondaryLocations',
      displayName: 'Secondary Locations',
      formatting: 'location',
    }),
  ].map((cm) => [cm.path, cm])
);

export const defaultTearSheetCompanySummaryPref = (() => {
  const availableFields = [
    'company.foundedDate',
    'company.lastPostMoney',
    'initialPostMoney',
    'normalizedMostRecentStage',
    'participatingLastRoundSeries',
    'company.mostRcentRound',
    'participatingRound',
    'participatingRoundDate',
    'company.mostRecetRoundDate',
    'normalizedMostRecentStageDate',
    'investmentStatus',
    'company.ceoName',
    'company.website',
    'company.investors',
    'company.primaryLocation',
    'company.secondaryLocations',
    'company.totalRaisedToDate',
    'company.sector.displayName',
    'company.dealLeadId',
    'company.dealTeamIds',
    'company.impliedEquityValue',
    'company.internalSourceId',
    'company.source',
    'initialInvestmentDate',
    'company.sourceCompany',
    'company.boardDirectorId',
    'company.boardStatus',
    'company.acquirer.fields.name',
    'company.fullyDilutedShares',
  ];

  return createPanelPreferences({
    availableFields,
    selectedFields: availableFields.slice(0, 15),
  });
})();

const defaultTearSheetInvestmentSummaryPref = createPanelPreferences({
  selectedFields: [
    'moic',
    'ownerShipPercentage',
    'irr',
    'amountInvested',
    'fmv',
    'impliedEquityValue',
    'reserves',
    'exitDate',
    'exitValuation',
    'exitType',
    'initialPreMoney',
    'exitOwnershipPercentage',
    'entryOwnership',
    'distributions',
    'convertedOwnership',
    'percentOfFund',
    'rtfe',
  ],
});

// we will need to keep this in sync with the companyFundMetricsFields on BE
export function availableInvestmentSummaryFields() {
  return {
    acquisitionDate: yup.date(),
    acquisitionShares: yup.number(),
    initialInvestmentRound: yup.number(),
    amountInvested: usdShortField().label('Total Cost'),
    companyId: yup.number().required(),
    convertedSharesHeld: yup.number(),
    convertedOwnership: percentField().label('Ownership % (converted)'),
    customData: yup.object(),
    dilutionAssumption: yup.number(),
    distributions: usdShortField().label('Realized Value'),
    entryOwnership: percentField().label('Entry Ownership %'),
    exitDate: dateField(),
    exitOwnershipPercentage: percentField().label('Exit Ownership %'),
    exitType: exitTypeField(),
    exitValuation: usdShortField(),
    fmv: usdShortField().label('FMV'),
    fmvBasis: yup.number(),
    fundId: yup.number().required().label('Fund').customMeta({
      renderer: RendererType.id,
      formatter: 'fund',
    }),
    id: yup.number().required(),
    impliedEquityValue: usdShortField(),
    initialInvestmentDate: yup.string(),
    initialPreMoney: usdShortField(),
    initialSharePrice: yup.number(),
    initialSharesHeld: yup.number(),
    irr: percentField().label('Gross IRR'),
    moic: multiplierField().label('MOIC'),
    noOfShares: yup.number(),
    ownerShipPercentage: percentField().label('Ownership %'),
    ownershipSource: yup.number(),
    projectedExitTiming: yup.number(),
    projectedMOIC: yup.number(),
    projectedProceeds: yup.number(),
    reserves: usdShortField(),
    percentOfFund: percentField().label('Percentage of Fund'),
    percentTotalValue: percentField(),
    rtfe: usdShortField().label('RTFE'),
    totalValue: usdShortField().label('Total Value'),
  };
}

const defaultCapTableSummaryPref = createPanelPreferences({
  selectedFields: ['cashSummary.cashRaised', 'debtSummary.cashRaised'],
});
// we will need to keep this in sync with src/tearsheets/tearsheets.service.ts::getCapitalizationSummarySection
export function availableCapitalizationSummaryFields() {
  return [
    createFormField({
      key: 'cashSummary.cashRaised',
      label: 'Equity',
      renderer: RendererType.number,
      formatter: 'currencyShort',
    }),
    createFormField({
      key: 'debtSummary.cashRaised',
      label: 'Debt',
      renderer: RendererType.number,
      formatter: 'currencyShort',
    }),
  ];
}

const defaultTearSheetPerformanceSummaryPref: TearSheetPerformanceSummaryPreferences = {
  period: KpiPeriod.quarter,
  relativePeriodDistances: [0],
  selectedFields: [],
  viewType: 'table',
};

const defaultTearSheetGeneralPref: TearSheetGeneralPreferences = {
  showClientLogo: true,
};

export const tearSheetGeneralPreferenceAtom = preferenceAtom(
  {
    preferenceKey: `${PreferenceKey.tearSheetGeneral}.default`,
    scope: PreferenceScope.ORGANIZATION,
  },
  undefined,
  defaultTearSheetGeneralPref
) as WritableAtom<TearSheetGeneralPreferences, [newValue: TearSheetGeneralPreferences], void>;

export const tearSheetCompanySummaryPreferenceAtom = preferenceAtom(
  {
    preferenceKey: `${PreferenceKey.tearSheetCompanySummary}.default`,
    scope: PreferenceScope.ORGANIZATION,
  },
  undefined,
  defaultTearSheetCompanySummaryPref
) as WritableAtom<PanelPreferences, [newValue: PanelPreferences], void>;

export const tearSheetInvestmentSummaryPreferenceAtom = preferenceAtom(
  {
    preferenceKey: `${PreferenceKey.tearSheetInvestmentSummary}.default`,
    scope: PreferenceScope.ORGANIZATION,
  },
  undefined,
  defaultTearSheetInvestmentSummaryPref
) as WritableAtom<PanelPreferences, [newValue: PanelPreferences], void>;

export const tearSheetPerformanceSummaryPreferenceAtom = preferenceAtom(
  {
    preferenceKey: `${PreferenceKey.tearSheetPerformanceSummary}.default`,
    scope: PreferenceScope.ORGANIZATION,
  },
  undefined,
  defaultTearSheetPerformanceSummaryPref
) as WritableAtom<
  TearSheetPerformanceSummaryPreferences,
  [newValue: TearSheetPerformanceSummaryPreferences],
  void
>;

export const tearSheetCapitalizationSummaryPreferenceAtom = preferenceAtom(
  {
    preferenceKey: `${PreferenceKey.tearSheetCapitalizationSummary}.default`,
    scope: PreferenceScope.ORGANIZATION,
  },
  undefined,
  defaultCapTableSummaryPref
) as WritableAtom<PanelPreferences, [newValue: PanelPreferences], void>;

export const tearSheetInvestmentThesisPreferenceAtom = preferenceAtom(
  {
    preferenceKey: `${PreferenceKey.tearSheetInvestmentThesis}.default`,
    scope: PreferenceScope.ORGANIZATION,
  },
  undefined,
  createPanelPreferences({ selectedFields: [], visible: true })
) as WritableAtom<PanelPreferences, [newValue: PanelPreferences], void>;

export const tearSheetPerformanceCommentaryPreferenceAtom = preferenceAtom(
  {
    preferenceKey: `${PreferenceKey.tearSheetPerformanceCommentary}.default`,
    scope: PreferenceScope.ORGANIZATION,
  },
  undefined,
  createPanelPreferences({ selectedFields: [], visible: true })
) as WritableAtom<PanelPreferences, [newValue: PanelPreferences], void>;

export const tearSheetCompanyDescriptionPreferenceAtom = preferenceAtom(
  {
    preferenceKey: `${PreferenceKey.tearSheetCompanyDescription}.default`,
    scope: PreferenceScope.ORGANIZATION,
  },
  undefined,
  createPanelPreferences({ selectedFields: [], visible: true })
) as WritableAtom<PanelPreferences, [newValue: PanelPreferences], void>;

// available fields for financing history section
export const FinancingHistoryFields = [
  'investmentDate',
  'round',
  'position',
  'raiseAmount',
  'postMoneyValuation',
  'leadInvestors',
  'coInvestors',
] as const;
export type FinancingHistoryField = (typeof FinancingHistoryFields)[number];
export const DefaultFinancingHistoryFields: FinancingHistoryField[] = FinancingHistoryFields.filter(
  (field) => field !== 'position' && field !== 'leadInvestors' && field !== 'coInvestors'
);

export const tearSheetFinancingHistoryPreferenceAtom = preferenceAtom(
  {
    preferenceKey: `${PreferenceKey.tearSheetFinancingHistory}.default`,
    scope: PreferenceScope.ORGANIZATION,
  },
  undefined,
  createPanelPreferences({ selectedFields: DefaultFinancingHistoryFields, visible: true })
) as WritableAtom<PanelPreferences, [newValue: PanelPreferences], void>;
